import { useEffect, useRef } from "react";

declare global {
  interface Window {
    Hls?: {
      isSupported: () => boolean;
      new (config?: unknown): {
        loadSource: (src: string) => void;
        attachMedia: (media: HTMLMediaElement) => void;
        destroy: () => void;
      };
    };
  }
}

type HlsInstance = {
  loadSource: (src: string) => void;
  attachMedia: (media: HTMLMediaElement) => void;
  destroy: () => void;
};

let hlsScriptPromise: Promise<void> | null = null;
function ensureHlsJsLoaded(): Promise<void> {
  if (globalThis.window?.Hls) return Promise.resolve();
  if (hlsScriptPromise) return hlsScriptPromise;

  hlsScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    // CDN: evita depender do node_modules (ambiente atual tem permissões inconsistentes)
    script.src = "https://cdn.jsdelivr.net/npm/hls.js@latest";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Falha ao carregar hls.js"));
    document.head.appendChild(script);
  });

  return hlsScriptPromise;
}

/**
 * Componente de vídeo com atributos para reprodução em WebViews móveis
 * (React Native WebView, WKWebView, X5).
 */
export function WebViewCompatVideo(
  props: Readonly<React.ComponentPropsWithoutRef<"video">>,
) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.setAttribute("webkit-playsinline", "");
      video.setAttribute("x5-playsinline", "");
      video.setAttribute("x5-video-player-type", "h5");
    }
  }, [props.src]);

  useEffect(() => {
    const video = videoRef.current;
    const src = props.src;
    if (!video || !src || typeof src !== "string") return;

    const isHls = src.toLowerCase().includes(".m3u8");
    if (!isHls) return;

    const canNative =
      Boolean(video.canPlayType("application/vnd.apple.mpegurl")) ||
      Boolean(video.canPlayType("application/x-mpegURL"));

    if (canNative) {
      video.src = src;
      return;
    }

    let hls: HlsInstance | null = null;
    let cancelled = false;

    ensureHlsJsLoaded()
      .then(() => {
        if (cancelled) return;
        const Hls = globalThis.window?.Hls;
        if (!Hls?.isSupported()) {
          // Último fallback: tenta setar src direto (pode funcionar em alguns webviews)
          video.src = src;
          return;
        }
        hls = new Hls() as HlsInstance;
        hls.loadSource(src);
        hls.attachMedia(video);
      })
      .catch(() => {
        video.src = src;
      });

    return () => {
      cancelled = true;
      try {
        hls?.destroy();
      } catch {
        // ignore
      }
    };
  }, [props.src]);

  const { children, ...rest } = props;
  return (
    <video ref={videoRef} playsInline {...rest}>
      <track kind="captions" src="" label="Nenhuma legenda" />
      {children}
    </video>
  );
}
